/**
 * Speech bubble manager
 * Handles creation, animation, and interaction of speech bubbles
 */

import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { areAnimationsEnabled } from '../../../utils/animation-settings'
import { getAudioManager } from '../../../features/audio'
import { getBubbleImagePath, applyResponsiveImage } from '../../../utils/pathResolver'
import { scrollManager } from '../../../utils/scroll'
import type { SpeechBubbleContext, BubbleImageData } from './types'

gsap.registerPlugin(ScrollTrigger)

/**
 * Get bubble image data for an action point
 */
export function getBubbleImageForActionPoint(
  data: SpeechBubbleContext['data'],
  actionPointIndex: number,
): BubbleImageData | null {
  const speechBubbles = data.speechBubbles || []
  if (actionPointIndex < 0 || actionPointIndex >= speechBubbles.length) {
    return null
  }

  const bubble = speechBubbles[actionPointIndex]
  if (!bubble) return null

  const bubbleImagePath = getBubbleImagePath(data.id, bubble.id)
  return {
    image: bubbleImagePath,
    width: bubble.width,
  }
}

/**
 * Get bubble ID for an action point
 */
export function getBubbleIdForActionPoint(data: SpeechBubbleContext['data'], actionPointIndex: number): string {
  const speechBubbles = data.speechBubbles || []
  const bubble = speechBubbles[actionPointIndex]
  return bubble?.id || `bubble-${actionPointIndex + 1}`
}

/**
 * Create a bubble image wrapped in a focusable button element.
 */
export function createBubbleImage(
  imagePath: string,
  xPercent: number,
  yPercent: number,
  widthPercent?: number,
  bubbleIndex?: number,
): HTMLButtonElement {
  const btn = document.createElement('button')
  btn.type = 'button'
  btn.className = 'bubble-image' + (bubbleIndex !== undefined ? ' bubble-image--clickable' : '')
  btn.style.position = 'absolute'
  btn.style.left = `${xPercent}%`
  btn.style.top = `${yPercent}%`
  btn.style.background = 'none'
  btn.style.border = 'none'
  btn.style.padding = '0'
  if (widthPercent !== undefined) {
    btn.style.width = `${widthPercent}%`
  } else {
    btn.style.maxWidth = '90%'
  }
  btn.style.zIndex = '10'
  btn.style.pointerEvents = bubbleIndex !== undefined ? 'auto' : 'none'
  if (bubbleIndex !== undefined) {
    btn.setAttribute('data-bubble-index', String(bubbleIndex))
    btn.setAttribute('aria-label', 'Přehrát znovu')
  }

  const img = document.createElement('img')
  img.src = imagePath
  img.alt = ''
  img.style.display = 'block'
  img.style.width = '100%'
  img.style.height = 'auto'
  img.style.pointerEvents = 'none'
  applyResponsiveImage(img)
  btn.appendChild(img)

  gsap.set(btn, { scale: 0, transformOrigin: 'center center' })
  return btn
}

/**
 * Animate bubble pop-out.
 * Waits for the inner <img> to be decoded so the animation is never invisible.
 */
export function animateBubblePopOut(
  bubbleImg: HTMLElement,
  bubbleIndex: number | undefined,
  config: SpeechBubbleContext['config'],
  onComplete?: (bubbleImg: HTMLElement, bubbleIndex: number) => void,
): void {
  const runAnimation = () => {
    gsap.to(bubbleImg, {
      scale: 1,
      duration: 0.4,
      ease: 'back.out(1.7)',
      onComplete: () => {
        if (bubbleIndex !== undefined && onComplete) {
          onComplete(bubbleImg, bubbleIndex)
        }
      },
    })

    if (config.bubble) {
      setupBubbleScrollAnimation(bubbleImg, config)
    }
  }

  const img = bubbleImg.querySelector('img') as HTMLImageElement | null
  if (!img || img.complete) {
    runAnimation()
    return
  }

  img.decode().then(runAnimation).catch(runAnimation)
}

/**
 * Setup bubble click and hover interactions
 */
export function setupBubbleClickAndHover(
  bubbleImg: HTMLElement,
  bubbleIndex: number,
  data: SpeechBubbleContext['data'],
): void {
  const speechBubbles = data.speechBubbles || []
  const bubble = speechBubbles[bubbleIndex]
  const soundIndex = bubble?.id ? parseInt(bubble.id.split('-').pop() || String(bubbleIndex + 1)) : bubbleIndex + 1

  bubbleImg.addEventListener('mouseenter', () => {
    // Use overwrite: 'auto' to only overwrite conflicting properties (scale), not the ScrollTrigger y animation
    gsap.to(bubbleImg, { scale: 0.95, duration: 0.2, ease: 'power2.out', overwrite: 'auto' })
  })
  bubbleImg.addEventListener('mouseleave', () => {
    // Use overwrite: 'auto' to only overwrite conflicting properties (scale), not the ScrollTrigger y animation
    gsap.to(bubbleImg, { scale: 1, duration: 0.2, ease: 'power2.out', overwrite: 'auto' })
  })
  bubbleImg.addEventListener('click', () => {
    getAudioManager().playBubbleSound(soundIndex, data.id)
    // Use overwrite: 'auto' to only overwrite conflicting properties (scale), not the ScrollTrigger y animation
    gsap.to(bubbleImg, { scale: 0.8, duration: 0.05, ease: 'power2.out', overwrite: 'auto' })
    gsap.to(bubbleImg, { scale: 1, duration: 0.12, delay: 0.05, ease: 'back.out(1.5)', overwrite: 'auto' })
  })
}

/**
 * Setup bubble scroll animation (parallax effect)
 */
function setupBubbleScrollAnimation(bubbleImg: HTMLElement, config: SpeechBubbleContext['config']): void {
  if (!config.bubble) return
  if (!areAnimationsEnabled()) return

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (bubbleImg.closest('.sticky-layout-container, .hscroll-sticky-container')) return

      const parallaxVerticalDistance = config.bubble!.parallaxVerticalDistance
      const yTo = -(parallaxVerticalDistance / 100) * window.innerHeight
      createBubbleParallaxTween(bubbleImg, yTo)
    })
  })
}

function createBubbleParallaxTween(bubbleImg: HTMLElement, yToPx: number): void {
  gsap.fromTo(
    bubbleImg,
    { y: 0 },
    {
      y: yToPx,
      ease: 'none',
      scrollTrigger: {
        trigger: bubbleImg,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true,
        invalidateOnRefresh: true,
      },
    },
  )
}

/**
 * Ensure bubble is visible in viewport.
 * @param options.skipScroll - If true, do not scroll (e.g. when action point was activated by mouse click).
 */
export function ensureBubbleVisible(bubbleImg: HTMLElement, options?: { skipScroll?: boolean }): void {
  if (options?.skipScroll) return
  setTimeout(() => {
    const rect = bubbleImg.getBoundingClientRect()
    const viewport = { height: window.innerHeight, width: window.innerWidth }

    if (isBubbleOutsideViewport(rect, viewport) || getVisibleHeightRatio(rect, viewport.height) < 0.5) {
      scrollBubbleIntoView(bubbleImg)
    }
  }, 450)
}

function isBubbleOutsideViewport(rect: DOMRect, viewport: { height: number; width: number }): boolean {
  return rect.bottom < 0 || rect.top > viewport.height || rect.right < 0 || rect.left > viewport.width
}

function getVisibleHeightRatio(rect: DOMRect, viewportHeight: number): number {
  const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0)
  return rect.height > 0 ? visibleHeight / rect.height : 0
}

function scrollBubbleIntoView(bubbleImg: HTMLElement): void {
  bubbleImg.scrollIntoView({ block: 'center', behavior: 'smooth', inline: 'nearest' })
}
